import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Modal, Form, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaUpload, FaEye } from 'react-icons/fa';
import { adSetsApi, adUsersApi } from '../../../services/sponsoredAdsApi';
import { handleDynamicRequest } from '../../../components/DyanamicRequest';
import { BASE_URL } from '../../../components/AppConfig';
import SetAlert from '../../../components/SetAlert';
import AWS from 'aws-sdk';

const S3_BUCKET = process.env.REACT_APP_S3_BUCKET;
const REGION = process.env.REACT_APP_REGION;
const ACCESS_KEY = process.env.REACT_APP_ACCESS_KEY;
const SECRET_ACCESS_KEY = process.env.REACT_APP_SECRET_ACCESS_KEY;

const AdSets = () => {
  const [adSets, setAdSets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAdsModal, setShowAdsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedAdSet, setSelectedAdSet] = useState(null);
  const [selectedAdSetAds, setSelectedAdSetAds] = useState([]);
  const [previewAd, setPreviewAd] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alertMessage, setAlertMessage] = useState({ success: '', error: '' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [loadingAds, setLoadingAds] = useState(false);

  const [formData, setFormData] = useState({
    adSetName: '',
    brandName: '',
    brandLogoUrl: '',
    ctaColor: '#000000',
    adUserId: '',
    adUserName: '',
    adUserBrandName: ''
  });

  useEffect(() => {
    fetchAdSets();
    fetchUsers();
  }, []);

  const fetchAdSets = async () => {
    setLoading(true);
    try {
      const response = await adSetsApi.getAll();
      console.log('Ad Sets response:', response);
      
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(adSet => ({
              id: adSet.id,
              adSetName: adSet.ad_set_name,
              brandName: adSet.brand_name,
              brandLogoUrl: adSet.brand_logo_url,
              ctaColor: adSet.cta_color,
              adUserId: adSet.ad_user_id,
              adUserName: adSet.ad_user_name,
              adUserBrandName: adSet.ad_user_brand_name,
              createdAt: adSet.created_at,
              updatedAt: adSet.updated_at
            }))
          : [];
        console.log('Transformed data:', transformedData);
        setAdSets(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch ad sets' }));
        setAdSets([]);
      }
    } catch (error) {
      console.error('Error fetching ad sets:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching ad sets' }));
      setAdSets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adUsersApi.getAll();
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(user => ({
              id: user.id,
              username: user.username,
              brand_name: user.brand_name,
              brand_details: user.brand_details
            }))
          : [];
        setUsers(transformedData);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const handleShowModal = (adSet = null) => {
    if (adSet) {
      setFormData({
        adSetName: adSet.adSetName,
        brandName: adSet.brandName,
        brandLogoUrl: adSet.brandLogoUrl,
        ctaColor: adSet.ctaColor,
        adUserId: adSet.adUserId?.toString() || '',
        adUserName: adSet.adUserName || '',
        adUserBrandName: adSet.adUserBrandName || ''
      });
      setSelectedAdSet(adSet);
    } else {
      setFormData({
        adSetName: '',
        brandName: '',
        brandLogoUrl: '',
        ctaColor: '#000000',
        adUserId: '',
        adUserName: '',
        adUserBrandName: ''
      });
      setSelectedAdSet(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdSet(null);
    setFormData({
      adSetName: '',
      brandName: '',
      brandLogoUrl: '',
      ctaColor: '#000000',
      adUserId: '',
      adUserName: '',
      adUserBrandName: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAdUserChange = (e) => {
    const selectedUserId = e.target.value;
    const selectedUser = users.find(user => user.id.toString() === selectedUserId);
    
    setFormData(prev => ({
      ...prev,
      adUserId: selectedUser ? selectedUser.id : '',
      adUserName: selectedUser ? selectedUser.username : '',
      adUserBrandName: selectedUser ? selectedUser.brand_name : ''
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const s3 = new AWS.S3({
        accessKeyId: ACCESS_KEY,
        secretAccessKey: SECRET_ACCESS_KEY,
        region: REGION,
      });

      const folderPath = `sponsored-ads/adsets/${formData.brandName}/`;
      const fileExtension = file.name.split('.').pop();
      const sanitizedBrandName = formData.brandName.replace(/\s+/g, '_');

      const params = {
        Bucket: S3_BUCKET,
        Key: `${folderPath}${sanitizedBrandName}_logo.${fileExtension}`,
        Body: file,
      };

      const uploadResponse = await s3.upload(params).promise();
      
      setFormData(prev => ({
        ...prev,
        brandLogoUrl: uploadResponse.Location
      }));
      
      setAlertMessage(prev => ({
        ...prev,
        success: 'Logo uploaded successfully',
        error: ''
      }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      setAlertMessage(prev => ({
        ...prev,
        error: 'Failed to upload logo',
        success: ''
      }));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const apiData = {
        ad_set_name: formData.adSetName,
        brand_name: formData.brandName,
        brand_logo_url: formData.brandLogoUrl,
        cta_color: formData.ctaColor,
        ad_user_id: parseInt(formData.adUserId)
      };
      
      const response = await (selectedAdSet 
        ? adSetsApi.update(selectedAdSet.id, apiData)
        : adSetsApi.create(apiData));

      if (response.success) {
        window.location.reload();
      } else {
        setAlertMessage(prev => ({ 
          ...prev, 
          error: response.message || `Failed to ${selectedAdSet ? 'update' : 'create'} ad set`,
          success: ''
        }));
      }
    } catch (error) {
      console.error('Error saving ad set:', error);
      setAlertMessage(prev => ({ 
        ...prev, 
        error: `Error ${selectedAdSet ? 'updating' : 'creating'} ad set`,
        success: ''
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ad set?')) {
      try {
        const response = await adSetsApi.delete(id);
        if (response.success) {
          window.location.reload();
        } else {
          setAlertMessage(prev => ({ 
            ...prev, 
            error: response.message || 'Failed to delete ad set',
            success: ''
          }));
        }
      } catch (error) {
        console.error('Error deleting ad set:', error);
        setAlertMessage(prev => ({ 
          ...prev, 
          error: 'Error deleting ad set',
          success: ''
        }));
      }
    }
  };

  const handleViewAds = async (adSet) => {
    setSelectedAdSet(adSet);
    setShowAdsModal(true);
    setLoadingAds(true);
    
    try {
      const response = await adSetsApi.getAdsByAdSetId(adSet.id);
      if (response.statusCode === "10000") {
        const transformedData = Array.isArray(response.data) 
          ? response.data.map(ad => ({
              id: ad.id,
              assetName: ad.asset_name,
              caption: ad.caption,
              media: ad.media,
              ctaText: ad.cta_text,
              ctaUrl: ad.cta_url,
              priority: ad.priority,
              adSetId: ad.ad_set_id,
              status: ad.status || 'active',
              createdAt: ad.created_at,
              updatedAt: ad.updated_at
            }))
          : [];
        setSelectedAdSetAds(transformedData);
      } else {
        setAlertMessage(prev => ({ ...prev, error: response.message || 'Failed to fetch ads for this ad set' }));
        setSelectedAdSetAds([]);
      }
    } catch (error) {
      console.error('Error fetching ads for ad set:', error);
      setAlertMessage(prev => ({ ...prev, error: 'Error fetching ads for this ad set' }));
      setSelectedAdSetAds([]);
    } finally {
      setLoadingAds(false);
    }
  };

  const handleCloseAdsModal = () => {
    setShowAdsModal(false);
    setSelectedAdSet(null);
    setSelectedAdSetAds([]);
  };

  const handlePreviewAd = (ad) => {
    setPreviewAd(ad);
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
    setPreviewAd(null);
  };

  const filteredAdSets = Array.isArray(adSets) ? adSets.filter(adSet => {
    const searchString = searchTerm.toLowerCase();
    return (
      adSet.adSetName.toLowerCase().includes(searchString) ||
      adSet.brandName.toLowerCase().includes(searchString) ||
      adSet.adUserName.toLowerCase().includes(searchString)
    );
  }) : [];

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ad-sets-container">
      <SetAlert alertMessage={alertMessage} setAlertMessage={setAlertMessage} />
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Ad Sets</h4>
          <Button variant="primary" onClick={() => handleShowModal()}>
            <FaPlus className="me-2" />
            New Ad Set
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={12}>
              <Form.Control
                type="text"
                placeholder="Search ad sets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Ad Set Name</th>
                <th>Brand</th>
                <th>Brand Logo</th>
                <th>CTA Color</th>
                <th>Ad User</th>
                <th>Ad User Brand</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdSets.map(adSet => (
                <React.Fragment key={adSet.id}>
                  <tr>
                  <td>{adSet.adSetName}</td>
                    <td>{adSet.brandName}</td>
                    <td>
                      {adSet.brandLogoUrl && (
                        <img 
                          src={adSet.brandLogoUrl} 
                          alt={adSet.brandName} 
                          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                        />
                      )}
                  </td>
                    <td>
                      <div 
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          backgroundColor: adSet.ctaColor,
                          border: '1px solid #ddd',
                          display: 'inline-block'
                        }} 
                      />
                      <span className="ms-2">{adSet.ctaColor}</span>
                  </td>
                    <td>{adSet.adUserName}</td>
                    <td>{adSet.adUserBrandName}</td>
                    <td>{new Date(adSet.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowModal(adSet)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(adSet.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
                  <tr>
                    <td colSpan="8" className="text-center py-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleViewAds(adSet)}
                      >
                        View Ads
                      </Button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedAdSet ? 'Edit Ad Set' : 'New Ad Set'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Ad Set Name</Form.Label>
              <Form.Control
                type="text"
                name="adSetName"
                value={formData.adSetName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand Name</Form.Label>
              <Form.Control
                type="text"
                name="brandName"
                value={formData.brandName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Brand Logo</Form.Label>
              <div className="d-flex align-items-center gap-2">
                {formData.brandLogoUrl && (
                  <img
                    src={formData.brandLogoUrl}
                    alt="Brand Logo Preview"
                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                  />
                )}
                <div className="flex-grow-1">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploadingLogo}
                  />
                </div>
                {uploadingLogo && (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Uploading...</span>
                  </div>
                )}
              </div>
              <Form.Control
                type="hidden"
                name="brandLogoUrl"
                value={formData.brandLogoUrl}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>CTA Color</Form.Label>
              <Form.Control
                type="color"
                name="ctaColor"
                value={formData.ctaColor}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ad User</Form.Label>
              <Form.Select
                name="adUserId"
                value={formData.adUserId}
                onChange={handleAdUserChange}
                required
              >
                <option value="">Select Ad User</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username} - {user.brand_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ad User Name</Form.Label>
              <Form.Control
                type="text"
                name="adUserName"
                value={formData.adUserName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ad User Brand Name</Form.Label>
              <Form.Control
                type="text"
                name="adUserBrandName"
                value={formData.adUserBrandName}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedAdSet ? 'Update' : 'Create'} Ad Set
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showAdsModal} onHide={handleCloseAdsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ads for {selectedAdSet?.adSetName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAds ? (
            <div className="text-center p-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : selectedAdSetAds.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Asset Name</th>
                  <th>Caption</th>
                  <th>Media</th>
                  <th>CTA Text</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedAdSetAds.map(ad => (
                  <tr key={ad.id}>
                    <td>{ad.assetName}</td>
                    <td>{ad.caption}</td>
                    <td>
                      {ad.media && (
                        <img 
                          src={ad.media} 
                          alt={ad.assetName} 
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      )}
                    </td>
                    <td>{ad.ctaText}</td>
                    <td>{ad.priority}</td>
                    <td>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handlePreviewAd(ad)}
                      >
                        <FaEye />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center p-3">
              <p>No ads found for this ad set.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAdsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreviewModal} onHide={handleClosePreview} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ad Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewAd && (
            <div className="ad-preview" style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px',
              overflow: 'hidden',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              <div className="ad-preview-header p-3" style={{ 
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div className="d-flex align-items-center">
                  {selectedAdSet?.brandLogoUrl ? (
                    <img
                      src={selectedAdSet.brandLogoUrl}
                      alt="Brand Logo"
                      className="me-2"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div 
                      className="me-2"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '50%',
                        backgroundColor: '#e9ecef',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <span style={{ color: '#6c757d', fontSize: '20px' }}>?</span>
                    </div>
                  )}
                  <div>
                    <h6 className="mb-0">{selectedAdSet?.brandName || 'Brand Name'}</h6>
                  </div>
                </div>
                <div>
                  <small className="text-muted">Sponsored</small>
                </div>
              </div>
              
              <div className="ad-preview-content p-3">
                <p className="mb-3">{previewAd.caption}</p>
              </div>
              
              <div className="ad-preview-media" style={{ position: 'relative' }}>
                {previewAd.media && (
                  <div style={{ 
                    width: '100%', 
                    height: '300px', 
                    backgroundColor: '#f8f9fa',
                    position: 'relative'
                  }}>
                    <img
                      src={previewAd.media}
                      alt={previewAd.assetName}
                      style={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{ 
                      position: 'absolute', 
                      bottom: '0', 
                      left: '0', 
                      right: '0',
                      zIndex: 10
                    }}>
                      {(() => {
                        // Get the CTA color from the ad set
                        const ctaColor = selectedAdSet?.ctaColor || '#000000';
                        
                        return (
                          <button
                            className="btn"
                            style={{ 
                              backgroundColor: `${ctaColor}CC`, // Adding CC for 80% opacity
                              borderColor: ctaColor,
                              color: '#ffffff',
                              padding: '12px 16px',
                              width: '100%',
                              borderRadius: '0',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '16px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                            onClick={() => window.open(previewAd.ctaUrl, '_blank')}
                          >
                            <span>{previewAd.ctaText}</span>
                            <span style={{ fontSize: '18px' }}>→</span>
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdSets; 